import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaClock,
  FaPaperPlane,
  FaCheck,
  FaPlay,
  FaSpinner,
  FaSyncAlt,
  FaStickyNote,
  FaExpand,
  FaCompress,
  FaWifi,
  FaExclamationTriangle,
  FaFilter,
  FaBookmark,
  FaKeyboard,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Loader/Loader';
import './ExamPage.css';


const getSelectedSubjectKey = (item) => `${item.category}__${item.subcategory}__${item.subject}`;

const SECTION_LABELS = {
  mcq: 'MCQ',
  'one-word': 'One Word',
  'short-answer': 'Short Answer',
  descriptive: 'Scenario',
  'long-answer': 'Long Answer',
};

const TIME_WARNINGS = [1800, 600, 300, 60];

const getQuestionStatus = ({ index, answers, reviewMarkedQuestions, visitedQuestions, currentQuestion }) => {
  const isAnswered = Boolean(answers[index]?.userAnswer?.trim());
  const isMarked = reviewMarkedQuestions.includes(index);
  const isVisited = visitedQuestions.includes(index);
  const isCurrent = currentQuestion === index;
  return { isAnswered, isMarked, isVisited, isCurrent };
};

const getSaveStateText = (saveState, isOnline, lastSavedAt) => {
  if (!isOnline || saveState === 'offline') return 'Offline • Changes pending sync';
  if (saveState === 'saving') return 'Online • Saving latest answer...';
  if (saveState === 'error') return 'Online • Last save failed';
  if (saveState === 'saved' && lastSavedAt) {
    return `Online • Saved at ${new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  return 'Online • All changes synced';
};

const ExamPage = () => {
  const navigate = useNavigate();
  const { examId } = useParams();
  const { token } = useAuth();

  // Setup state
  const [step, setStep] = useState(examId ? 'loading' : 'setup');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedSubjectKey, setSelectedSubjectKey] = useState('');
  const [level, setLevel] = useState('graduation');
  const [activeExam, setActiveExam] = useState(null);

  const selectedSubjectEntry = useMemo(
    () => selectedSubjects.find((item) => getSelectedSubjectKey(item) === selectedSubjectKey) || null,
    [selectedSubjects, selectedSubjectKey]
  );

  // Exam state
  const [exam, setExam] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [reviewMarkedQuestions, setReviewMarkedQuestions] = useState([]);
  const [visitedQuestions, setVisitedQuestions] = useState([]);
  const [scratchNotes, setScratchNotes] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [questionFilter, setQuestionFilter] = useState('all');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [saveState, setSaveState] = useState('saved');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [hasSubmissionBackup, setHasSubmissionBackup] = useState(false);

  const timerRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const warningSetRef = useRef(new Set());
  const timedOutAutoSubmitRef = useRef(false);
  const previousTimeLeftRef = useRef(null);
  const previousTabSwitchCountRef = useRef(0);

  const submitRecoveryKey = useMemo(
    () => (exam?._id ? `exam-submit-recovery-${exam._id}` : null),
    [exam?._id]
  );

  // Fetch user's selected subjects
  useEffect(() => {
    const fetchSelectedSubjects = async () => {
      try {
        const res = await axios.get(api.getUserSubjectSelection, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          const subjects = res.data.data?.subjects || [];
          setSelectedSubjects(subjects);
          if (subjects.length > 0) {
            setSelectedSubjectKey(getSelectedSubjectKey(subjects[0]));
          }
        }
      } catch (error) {
        console.error('Error fetching selected subjects:', error);
        toast.error('Failed to load selected subjects');
      }
    };
    fetchSelectedSubjects();
  }, [token]);

  // Load active exam (resume) when opening setup directly
  useEffect(() => {
    if (examId) return;
    const fetchActiveExam = async () => {
      try {
        const res = await axios.get(api.getActiveExam, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success && res.data.data) {
          setActiveExam(res.data.data);
        }
      } catch (error) {
        console.error('Error fetching active exam:', error);
      }
    };
    fetchActiveExam();
  }, [examId, token]);

  const hydrateExamState = useCallback((examData) => {
    setExam(examData);
    const normalizedAnswers = examData.questions.map((_, i) => ({
      questionIndex: i,
      userAnswer: examData.answers?.find((a) => a.questionIndex === i)?.userAnswer || examData.answers?.[i]?.userAnswer || '',
    }));
    setAnswers(normalizedAnswers);
    setReviewMarkedQuestions(examData.reviewMarkedQuestions || []);
    setVisitedQuestions(examData.visitedQuestions || []);
    setCurrentQuestion(Math.min(examData.currentQuestionIndex || 0, Math.max(0, examData.questions.length - 1)));
    setScratchNotes(examData.scratchNotes || '');
    setTabSwitchCount(examData.tabSwitchCount || 0);
    previousTabSwitchCountRef.current = examData.tabSwitchCount || 0;
    const initialRemaining =
      typeof examData.remainingSeconds === 'number'
        ? examData.remainingSeconds
        : examData.timeLimitMinutes * 60;
    setTimeLeft(initialRemaining);
    previousTimeLeftRef.current = initialRemaining;
    timedOutAutoSubmitRef.current = false;
    warningSetRef.current = new Set();
  }, []);

  // Load existing exam if examId provided
  useEffect(() => {
    if (!examId) return;
    const loadExam = async () => {
      try {
        const res = await axios.get(api.getExamById(examId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          const examData = res.data.data;
          hydrateExamState(examData);
          if (examData.status === 'evaluated') {
            navigate(`/university/result/${examId}`);
            return;
          }
          if (examData.status === 'in-progress') {
            setStep('exam');
          } else if (examData.status === 'created') {
            setStep('ready');
          } else {
            setStep('submitted');
          }
        }
      } catch (error) {
        console.error('Error loading exam:', error);
        toast.error('Failed to load exam');
        navigate('/university/dashboard');
      }
    };
    loadExam();
  }, [examId, token, navigate, hydrateExamState]);

  useEffect(() => {
    const syncOnline = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', syncOnline);
    window.addEventListener('offline', syncOnline);
    return () => {
      window.removeEventListener('online', syncOnline);
      window.removeEventListener('offline', syncOnline);
    };
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (step !== 'exam') return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        setTabSwitchCount((prev) => prev + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [step]);

  useEffect(() => {
    if (step !== 'exam') return;
    if (tabSwitchCount > previousTabSwitchCountRef.current && tabSwitchCount <= 5) {
      toast.warn(`Tab switched detected (${tabSwitchCount}). Stay on this page during exam.`);
    }
    previousTabSwitchCountRef.current = tabSwitchCount;
  }, [tabSwitchCount, step]);

  // Timer
  useEffect(() => {
    if (step !== 'exam' || timeLeft <= 0) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [step]);

  useEffect(() => {
    if (step !== 'exam' || timeLeft <= 0) return;

    if (previousTimeLeftRef.current === null) {
      previousTimeLeftRef.current = timeLeft;
      return;
    }

    const previous = previousTimeLeftRef.current;
    TIME_WARNINGS.forEach((threshold) => {
      if (previous > threshold && timeLeft <= threshold && !warningSetRef.current.has(threshold)) {
        warningSetRef.current.add(threshold);
        const minutes = Math.floor(threshold / 60);
        toast.warn(minutes > 1 ? `${minutes} minutes left` : '1 minute left');
      }
    });
    previousTimeLeftRef.current = timeLeft;
  }, [timeLeft, step]);

  useEffect(() => {
    if (step !== 'exam') return;
    setVisitedQuestions((prev) => (prev.includes(currentQuestion) ? prev : [...prev, currentQuestion]));
  }, [currentQuestion, step]);

  useEffect(() => {
    if (step !== 'exam' || !submitRecoveryKey) return;
    setHasSubmissionBackup(Boolean(localStorage.getItem(submitRecoveryKey)));
  }, [step, submitRecoveryKey]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const sectionGroups = useMemo(() => {
    if (!exam?.questions?.length) return [];
    const map = new Map();
    exam.questions.forEach((q, i) => {
      const key = q.type || 'other';
      const existing = map.get(key) || { key, label: SECTION_LABELS[key] || key, count: 0, firstIndex: i };
      existing.count += 1;
      map.set(key, existing);
    });
    return Array.from(map.values());
  }, [exam]);

  const getFilterCount = useCallback(
    (filter) => {
      if (!exam?.questions?.length) return 0;
      return exam.questions.filter((_, i) => {
        const status = getQuestionStatus({
          index: i,
          answers,
          reviewMarkedQuestions,
          visitedQuestions,
          currentQuestion,
        });
        if (filter === 'all') return true;
        if (filter === 'answered') return status.isAnswered;
        if (filter === 'unanswered') return !status.isAnswered;
        if (filter === 'review') return status.isMarked;
        if (filter === 'visited') return status.isVisited;
        return true;
      }).length;
    },
    [exam, answers, reviewMarkedQuestions, visitedQuestions, currentQuestion]
  );

  const filteredIndexes = useMemo(() => {
    if (!exam?.questions?.length) return [];
    return exam.questions
      .map((_, i) => i)
      .filter((i) => {
        const status = getQuestionStatus({
          index: i,
          answers,
          reviewMarkedQuestions,
          visitedQuestions,
          currentQuestion,
        });
        if (questionFilter === 'answered') return status.isAnswered;
        if (questionFilter === 'unanswered') return !status.isAnswered;
        if (questionFilter === 'review') return status.isMarked;
        if (questionFilter === 'visited') return status.isVisited;
        return true;
      });
  }, [exam, answers, reviewMarkedQuestions, visitedQuestions, currentQuestion, questionFilter]);

  const answeredCount = useMemo(() => answers.filter((a) => a.userAnswer.trim()).length, [answers]);

  const saveProgress = useCallback(
    async (reason = 'auto') => {
      if (!exam || step !== 'exam' || submitting || timeLeft <= 0) return false;
      if (!isOnline) {
        setSaveState('offline');
        return false;
      }

      try {
        setSaveState('saving');
        const payload = {
          answers,
          currentQuestionIndex: currentQuestion,
          reviewMarkedQuestions,
          visitedQuestions,
          scratchNotes,
          tabSwitchCount,
        };
        const res = await axios.post(api.saveExamProgress(exam._id), payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSaveState('saved');
        setLastSavedAt(res.data?.data?.savedAt || new Date().toISOString());
        if (reason === 'manual') toast.success('Progress saved');
        return true;
      } catch (error) {
        console.error('Auto-save failed:', error);
        setSaveState('error');
        return false;
      }
    },
    [exam, step, isOnline, answers, currentQuestion, reviewMarkedQuestions, visitedQuestions, scratchNotes, tabSwitchCount, token, submitting, timeLeft]
  );

  useEffect(() => {
    if (step !== 'exam' || submitting || timeLeft <= 0) return;
    setSaveState(isOnline ? 'saving' : 'offline');
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      saveProgress();
    }, 1200);

    return () => clearTimeout(autoSaveTimerRef.current);
  }, [answers, currentQuestion, reviewMarkedQuestions, visitedQuestions, scratchNotes, tabSwitchCount, step, isOnline, saveProgress, submitting, timeLeft]);

  useEffect(() => {
    if (step !== 'exam' || submitting || timeLeft <= 0) return;
    const interval = setInterval(() => {
      saveProgress();
    }, 30000);
    return () => clearInterval(interval);
  }, [step, saveProgress, submitting, timeLeft]);

  useEffect(() => {
    if (step !== 'exam') return;
    if (timeLeft <= 0 || submitting) return;
    const onKeydown = (event) => {
      const tag = event.target.tagName?.toLowerCase();
      const isTypingField = tag === 'textarea' || tag === 'input';

      if (event.key === 'Escape' && focusMode) {
        setFocusMode(false);
        return;
      }

      if (isTypingField) return;

      if (event.key === 'ArrowRight') {
        setCurrentQuestion((prev) => Math.min((exam?.questions?.length || 1) - 1, prev + 1));
      }
      if (event.key === 'ArrowLeft') {
        setCurrentQuestion((prev) => Math.max(0, prev - 1));
      }
      if (event.key.toLowerCase() === 'm') {
        toggleMarkForReview(currentQuestion);
      }
      if (event.key.toLowerCase() === 'f') {
        setFocusMode((prev) => !prev);
      }
      if (event.key.toLowerCase() === 'n') {
        setNotesOpen((prev) => !prev);
      }

      if (/^[1-9]$/.test(event.key)) {
        const optionIndex = Number(event.key) - 1;
        const q = exam?.questions?.[currentQuestion];
        if (q?.type === 'mcq' && q.options?.[optionIndex]) {
          updateAnswer(currentQuestion, q.options[optionIndex]);
        }
      }
    };

    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, [step, exam, currentQuestion, focusMode, timeLeft, submitting]);

  // Create exam
  const handleCreateExam = async () => {
    if (activeExam?.status === 'in-progress' || activeExam?.status === 'created') {
      toast.info('You already have an active exam. Please open it first.');
      navigate(`/university/exam/${activeExam._id}`);
      return;
    }

    if (!selectedSubjectEntry) {
      toast.warning('Please select a subject');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        api.createExam,
        {
          subject: selectedSubjectEntry.subject,
          category: selectedSubjectEntry.category,
          subcategory: selectedSubjectEntry.subcategory,
          level,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const examData = res.data.data;
        hydrateExamState({
          ...examData,
          answers: examData.questions.map((_, i) => ({ questionIndex: i, userAnswer: '' })),
          reviewMarkedQuestions: [],
          visitedQuestions: [],
          currentQuestionIndex: 0,
          scratchNotes: '',
          tabSwitchCount: 0,
        });
        setStep('ready');
      }
    } catch (error) {
      console.error('Error creating exam:', error);
      if (error.response?.status === 409 && error.response?.data?.existingExamId) {
        toast.info(error.response?.data?.message || 'Resuming your active exam.');
        navigate(`/university/exam/${error.response.data.existingExamId}`);
        return;
      }
      toast.error(error.response?.data?.message || 'Failed to create exam');
    } finally {
      setCreating(false);
    }
  };

  // Start exam
  const handleStartExam = async () => {
    try {
      const res = await axios.post(
        api.startExam(exam._id),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        hydrateExamState({ ...res.data.data, remainingSeconds: res.data.data.timeLimitMinutes * 60 });
        setVisitedQuestions([0]);
        setStep('exam');
      }
    } catch (error) {
      console.error('Error starting exam:', error);
      toast.error('Failed to start exam');
    }
  };

  // Update answer
  const updateAnswer = (questionIndex, value) => {
    if (timeLeft <= 0 || submitting) return;
    setAnswers((prev) =>
      prev.map((a, i) => (i === questionIndex ? { ...a, userAnswer: value } : a))
    );
  };

  const toggleMarkForReview = (index) => {
    if (timeLeft <= 0 || submitting) return;
    setReviewMarkedQuestions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const persistSubmissionBackup = (payload) => {
    if (!submitRecoveryKey) return;
    localStorage.setItem(
      submitRecoveryKey,
      JSON.stringify({
        ...payload,
        updatedAt: new Date().toISOString(),
      })
    );
    setHasSubmissionBackup(true);
  };

  const clearSubmissionBackup = () => {
    if (!submitRecoveryKey) return;
    localStorage.removeItem(submitRecoveryKey);
    setHasSubmissionBackup(false);
  };

  // Submit exam
  const executeSubmit = async (providedPayload = null) => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);

    try {
      const payload = providedPayload || { answers };
      persistSubmissionBackup(payload);

      await axios.post(
        api.submitExam(exam._id),
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep('submitted');

      // Evaluate
      const evalRes = await axios.post(
        api.evaluateExam(exam._id),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (evalRes.data.success) {
        clearSubmissionBackup();
        toast.success('Exam evaluated successfully!');
        navigate(`/university/result/${exam._id}`);
      }
    } catch (error) {
      console.error('Error submitting exam:', error);
      toast.error('Submit failed. Your answers are safe. Retry submission.');
      setHasSubmissionBackup(true);
    } finally {
      setSubmitting(false);
      setShowSubmitConfirm(false);
    }
  };

  useEffect(() => {
    if (step !== 'exam' || !exam) return;
    if (timeLeft > 0 || timedOutAutoSubmitRef.current) return;
    timedOutAutoSubmitRef.current = true;
    toast.warn('Time is up. Submitting your exam now...');
    executeSubmit({ answers });
  }, [step, exam, timeLeft, answers]);

  const handleSubmit = async () => {
    await saveProgress('manual');
    setShowSubmitConfirm(true);
  };

  const retrySubmissionFromBackup = async () => {
    if (!submitRecoveryKey) return;
    try {
      const raw = localStorage.getItem(submitRecoveryKey);
      if (!raw) {
        toast.info('No pending submission found');
        return;
      }
      const data = JSON.parse(raw);
      await executeSubmit({ answers: data.answers || answers });
    } catch (error) {
      console.error('Failed to retry submission:', error);
      toast.error('Could not retry saved submission');
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen failed:', error);
    }
  };

  // Loading state
  if (step === 'loading') return <Loader message="Loading exam..." />;

  // Setup step
  if (step === 'setup') {
    return (
      <div className="exam-page">
        <header className="exam-header">
          <button className="exam-back-btn" onClick={() => navigate('/university/dashboard')}>
            <FaArrowLeft /> Back
          </button>
          <h1>New Exam</h1>
          <div></div>
        </header>

        <div className="exam-setup">
          <div className="exam-setup-card">
            <h2>Configure Your Exam</h2>

            {activeExam && (
              <div className="exam-resume-card">
                <div>
                  <h3>Resume Existing Exam</h3>
                  <p>
                    {activeExam.subject} • {activeExam.level} • {activeExam.status === 'in-progress' ? 'In Progress' : 'Ready to Start'}
                  </p>
                </div>
                <button
                  type="button"
                  className="exam-resume-btn"
                  onClick={() => navigate(`/university/exam/${activeExam._id}`)}
                >
                  <FaSyncAlt /> {activeExam.status === 'in-progress' ? 'Resume Exam' : 'Open Exam'}
                </button>
              </div>
            )}

            {/* Selected subject from user's saved track */}
            <div className="exam-field">
              <label>Selected Subject</label>
              <div className="exam-subject-chip-list">
                {selectedSubjects.map((item) => {
                  const key = getSelectedSubjectKey(item);
                  const isActive = selectedSubjectKey === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`exam-subject-chip ${isActive ? 'active' : ''}`}
                      onClick={() => setSelectedSubjectKey(key)}
                    >
                      <span className="exam-subject-chip-name">{item.subject}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedSubjects.length === 0 && (
              <div className="exam-subjects-note">
                No selected subjects found. Please add subjects first.
                <button
                  type="button"
                  className="exam-link-btn"
                  onClick={() => navigate('/university/subjects')}
                >
                  Select Subjects
                </button>
              </div>
            )}

            {/* Level */}
            <div className="exam-field">
              <label>Choose Your Exam Tier</label>
              <div className="exam-level-options">
                {['diploma', 'graduation', 'postgraduation'].map((d) => (
                  <button
                    key={d}
                    className={`exam-diff-btn ${level === d ? 'active' : ''} exam-diff-${d}`}
                    onClick={() => setLevel(d)}
                  >
                    {d === 'diploma' ? 'Diploma' : d === 'postgraduation' ? 'Post Graduation' : 'Graduation'}
                  </button>
                ))}
              </div>
            </div>

            {/* Exam Info */}
            <div className="exam-field exam-info-banner">
              <label>Exam Overview</label>
              <div className="exam-overview-list">
                <span><strong>Total Marks:</strong> 100</span>
                <span><strong>Duration:</strong> 120 Minutes</span>
                <span><strong>Question Types:</strong> MCQ, Short Answer, Case Study / Scenario</span>
              </div>
            </div>

            <button
              className="exam-create-btn"
              onClick={handleCreateExam}
              disabled={!selectedSubjectEntry || creating}
            >
              {creating ? (
                <>
                  <FaSpinner className="spinning" /> Generating Exam...
                </>
              ) : (
                <>
                  <FaPlay /> Generate Exam
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ready step - exam created, not started
  if (step === 'ready' && exam) {
    return (
      <div className="exam-page">
        <header className="exam-header">
          <button className="exam-back-btn" onClick={() => navigate('/university/dashboard')}>
            <FaArrowLeft /> Back
          </button>
          <h1>Exam Ready</h1>
          <div></div>
        </header>
        <div className="exam-ready">
          <div className="exam-ready-card">
            <h2>{exam.subject}</h2>
            <div className="exam-ready-info">
              <p><strong>Category:</strong> {exam.category} → {exam.subcategory}</p>
              <p><strong>Level:</strong> <span className={`exam-diff-tag exam-diff-${exam.level}`}>{exam.level}</span></p>
              {/* <p><strong>Questions:</strong> {exam.questions.length}</p> */}
              <p><strong>Total Marks:</strong> {exam.totalMarks}</p>
              <p><strong>Time Limit:</strong> {exam.timeLimitMinutes} minutes</p>
            </div>
            <div className="exam-ready-rules">
              <h3>Instructions</h3>
              <ul>
                <li>Answer all questions within the time limit</li>
                <li>Answers, review marks, and notes auto-save during exam</li>
                <li>Once submitted, answers cannot be changed</li>
                <li>AI will evaluate your answers after submission</li>
              </ul>
            </div>
            <button className="exam-start-btn" onClick={handleStartExam}>
              <FaPlay /> Start Exam
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Exam step - answering questions
  if (step === 'exam' && exam) {
    const question = exam.questions[currentQuestion];
    const unansweredCount = exam.questions.length - answeredCount;
    const progressPercent = Math.round((answeredCount / exam.questions.length) * 100);
    const currentMarked = reviewMarkedQuestions.includes(currentQuestion);
    const isExamLocked = submitting || timeLeft <= 0;

    return (
      <div className={`exam-page ${focusMode ? 'exam-focus-mode' : ''}`}>
        <header className="exam-header exam-active-header">
          <div className="exam-header-info">
            <h2>{exam.subject}</h2>
            <span className={`exam-diff-tag exam-diff-${exam.level}`}>{exam.level}</span>
          </div>
          <div className="exam-header-tools">
            <div className="exam-online-state" title={getSaveStateText(saveState, isOnline, lastSavedAt)}>
              {isOnline ? <FaWifi /> : <FaExclamationTriangle />}
              <span>{getSaveStateText(saveState, isOnline, lastSavedAt)}</span>
            </div>
            <div className="exam-timer">
              <FaClock />
              <span className={timeLeft < 60 ? 'exam-timer-warning' : ''}>{formatTime(timeLeft)}</span>
            </div>
            <button type="button" className="exam-mini-btn" onClick={toggleFullscreen}>
              {isFullscreen ? <FaCompress /> : <FaExpand />} {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </button>
            <button type="button" className="exam-mini-btn" onClick={() => setFocusMode((prev) => !prev)}>
              <FaFilter /> {focusMode ? 'Normal Mode' : 'Focus Mode'}
            </button>
            <button type="button" className="exam-mini-btn" onClick={() => setNotesOpen((prev) => !prev)}>
              <FaStickyNote /> Notes
            </button>
          </div>
        </header>

        <div className="exam-progress-wrap">
          <div className="exam-progress-meta">
            <span>{answeredCount}/{exam.questions.length} answered</span>
            <span>{reviewMarkedQuestions.length} marked for review</span>
            <span>{tabSwitchCount} tab switches</span>
            <span><FaKeyboard /> Shortcuts: ← →, 1-9, M, N, F</span>
          </div>
          <div className="exam-progress-bar">
            <div className="exam-progress-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>
        </div>

        <div className="exam-section-nav">
          {sectionGroups.map((section) => (
            <button
              key={section.key}
              type="button"
              className="exam-section-btn"
              onClick={() => setCurrentQuestion(section.firstIndex)}
              disabled={isExamLocked}
            >
              {section.label} ({section.count})
            </button>
          ))}
        </div>

        <div className="exam-filter-row">
          {['all', 'answered', 'unanswered', 'review', 'visited'].map((filter) => (
            <button
              key={filter}
              type="button"
              className={`exam-filter-btn ${questionFilter === filter ? 'active' : ''}`}
              onClick={() => setQuestionFilter(filter)}
              disabled={isExamLocked}
            >
              {filter === 'review' ? 'Marked' : filter.charAt(0).toUpperCase() + filter.slice(1)} ({getFilterCount(filter)})
            </button>
          ))}
        </div>

        {/* Question Navigation */}
        <div className="exam-question-nav">
          {filteredIndexes.map((i) => {
            const status = getQuestionStatus({
              index: i,
              answers,
              reviewMarkedQuestions,
              visitedQuestions,
              currentQuestion,
            });
            return (
              <button
                key={i}
                className={`exam-q-dot ${status.isCurrent ? 'current' : ''} ${status.isAnswered ? 'answered' : ''} ${status.isMarked ? 'marked' : ''} ${status.isVisited ? 'visited' : ''}`}
                onClick={() => setCurrentQuestion(i)}
                title={`Question ${i + 1}`}
                disabled={isExamLocked}
              >
                {i + 1}
              </button>
            );
          })}
        </div>

        {filteredIndexes.length === 0 && (
          <div className="exam-filter-empty">No questions match this filter.</div>
        )}

        {hasSubmissionBackup && (
          <div className="exam-recovery-banner">
            <span>Submission previously failed. Your answers are safe.</span>
            <button type="button" onClick={retrySubmissionFromBackup}>Retry Submission</button>
          </div>
        )}

        {/* Question */}
        <div className="exam-question-container">
          <div className="exam-question-header">
            <span className="exam-q-number">Question {currentQuestion + 1} of {exam.questions.length}</span>
            <span className="exam-q-marks">{question.marks} mark{question.marks > 1 ? 's' : ''}</span>
            {question.topic && <span className="exam-q-topic">{question.topic}</span>}
            <button
              type="button"
              className={`exam-mark-btn ${currentMarked ? 'active' : ''}`}
              onClick={() => toggleMarkForReview(currentQuestion)}
              disabled={isExamLocked}
            >
              <FaBookmark /> {currentMarked ? 'Marked' : 'Mark for Review'}
            </button>
          </div>

          <p className="exam-question-text">{question.questionText}</p>

          {/* MCQ Options */}
          {question.type === 'mcq' && question.options?.length > 0 && (
            <div className="exam-options">
              {question.options.map((option, oi) => (
                <button
                  key={oi}
                  className={`exam-option ${answers[currentQuestion]?.userAnswer === option ? 'selected' : ''}`}
                  onClick={() => updateAnswer(currentQuestion, option)}
                  disabled={isExamLocked}
                >
                  <span className="exam-option-letter">{String.fromCharCode(65 + oi)}</span>
                  <span>{option.replace(/^[A-D]\)\s*/, '')}</span>
                  {answers[currentQuestion]?.userAnswer === option && <FaCheck className="exam-option-check" />}
                </button>
              ))}
            </div>
          )}

          {/* One-word Answer */}
          {question.type === 'one-word' && (
            <input
              type="text"
              className="exam-oneword-input"
              placeholder="Type your answer (one word or short phrase)..."
              value={answers[currentQuestion]?.userAnswer || ''}
              onChange={(e) => updateAnswer(currentQuestion, e.target.value)}
              disabled={isExamLocked}
            />
          )}

          {/* Short Answer */}
          {question.type === 'short-answer' && (
            <textarea
              className="exam-answer-textarea"
              placeholder="Write your answer (2-3 sentences)..."
              value={answers[currentQuestion]?.userAnswer || ''}
              onChange={(e) => updateAnswer(currentQuestion, e.target.value)}
              rows={4}
              disabled={isExamLocked}
            />
          )}

          {/* Descriptive */}
          {question.type === 'descriptive' && (
            <textarea
              className="exam-answer-textarea"
              placeholder="Write a detailed answer..."
              value={answers[currentQuestion]?.userAnswer || ''}
              onChange={(e) => updateAnswer(currentQuestion, e.target.value)}
              rows={8}
              disabled={isExamLocked}
            />
          )}

          {/* Long Answer */}
          {question.type === 'long-answer' && (
            <textarea
              className="exam-answer-textarea exam-long-answer"
              placeholder="Write a comprehensive, detailed response..."
              value={answers[currentQuestion]?.userAnswer || ''}
              onChange={(e) => updateAnswer(currentQuestion, e.target.value)}
              rows={12}
              disabled={isExamLocked}
            />
          )}

          {/* Navigation */}
          <div className="exam-nav-buttons">
            <button
              className="exam-nav-btn"
              onClick={() => setCurrentQuestion((prev) => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0 || isExamLocked}
            >
              Previous
            </button>

            {currentQuestion < exam.questions.length - 1 ? (
              <button
                className="exam-nav-btn exam-nav-next"
                onClick={() => setCurrentQuestion((prev) => Math.min(exam.questions.length - 1, prev + 1))}
                disabled={isExamLocked}
              >
                Next
              </button>
            ) : (
              <button
                className="exam-submit-btn"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="spinning" /> Evaluating...
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> Submit Exam ({answeredCount}/{exam.questions.length} answered)
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {notesOpen && (
          <aside className="exam-notes-panel">
            <div className="exam-notes-head">
              <h3><FaStickyNote /> Scratch Notes</h3>
              <button type="button" onClick={() => setNotesOpen(false)}>Close</button>
            </div>
            <p>Private notes only. Not submitted or evaluated.</p>
            <textarea
              value={scratchNotes}
              onChange={(e) => setScratchNotes(e.target.value)}
              placeholder="Write rough work, formulas, or answer outlines..."
              rows={14}
            />
            <small>{scratchNotes.length}/10000</small>
          </aside>
        )}

        {showSubmitConfirm && (
          <div className="exam-submit-modal-overlay">
            <div className="exam-submit-modal">
              <h3>Confirm Submission</h3>
              <p>Review before final submit:</p>
              <ul>
                <li>Answered: {answeredCount}</li>
                <li>Unanswered: {unansweredCount}</li>
                <li>Marked for review: {reviewMarkedQuestions.length}</li>
                <li>Time left: {formatTime(timeLeft)}</li>
              </ul>
              <div className="exam-submit-modal-actions">
                <button type="button" className="exam-nav-btn" onClick={() => setShowSubmitConfirm(false)}>
                  Go Back
                </button>
                <button type="button" className="exam-submit-btn" onClick={() => executeSubmit()} disabled={submitting}>
                  {submitting ? <><FaSpinner className="spinning" /> Submitting...</> : <><FaPaperPlane /> Submit Now</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Submitted - waiting
  if (step === 'submitted') {
    return (
      <div className="exam-page">
        <div className="exam-submitted">
          <FaSpinner className="spinning" style={{ fontSize: 40, color: '#2563eb' }} />
          <h2>Evaluating your exam...</h2>
          <p>Please wait while AI evaluates your answers.</p>
        </div>
      </div>
    );
  }

  return <Loader />;
};

export default ExamPage;
